import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/v5/components/ui/dialog";
import { Button } from "@/v5/components/ui/button";
import { Input } from "@/v5/components/ui/input";
import { Label } from "@/v5/components/ui/label";
import { Checkbox } from "@/v5/components/ui/checkbox";
import * as notesSvc from "@/v5/services/notes.service";
import { toast } from "@/store/toastStore";

interface SaveTemplateDialogProps {
  initialTitle: string;
  initialContent: string;
  noteTypeId: number;
  onSaved?: () => void | Promise<void>;
  onClose: () => void;
}

export function SaveTemplateDialog({ initialTitle, initialContent, noteTypeId, onSaved, onClose }: SaveTemplateDialogProps) {
  const [title, setTitle] = useState(initialTitle);
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!title.trim()) { toast.error("Template name is required"); return; }
    if (!noteTypeId) { toast.error("Pick a Note Type before saving as a template"); return; }
    setSaving(true);
    try {
      await notesSvc.saveAsTemplate({
        title,
        content: initialContent,
        subject: title,
        noteTypeId,
        isShared,
      });
      toast.success("Template saved");
      await onSaved?.();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save template";
      toast.error(msg.includes("already exists") ? "A template with this name already exists" : msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" /> Save as Template
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Template Name</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter template name..."
              autoFocus
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={isShared} onCheckedChange={(v) => setIsShared(!!v)} />
            Share with office (visible to all staff)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !title.trim()} className="gap-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
